import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  StatusBar,
  Image,
} from 'react-native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';
import { vs, spacing, fontSize, iconSize } from '../utils/responsive';

const AboutScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.headerBackground, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={iconSize.md} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.primary }]}>About</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          <Image
            source={require('../assets/trading_logo.jpeg')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        {/* App Info Card */}
        <View style={[styles.card, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
          <View style={styles.infoRow}>
            <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>App Name</Text>
            <Text style={[styles.infoValue, { color: colors.text }]}>TradingView</Text>
          </View>
          
          <View style={[styles.divider, { backgroundColor: colors.border }]} />
          
          <View style={styles.infoRow}>
            <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>Version</Text>
            <Text style={[styles.infoValue, { color: colors.text }]}>1.0.0</Text>
          </View>
          
          <View style={[styles.divider, { backgroundColor: colors.border }]} />
          
          <View style={styles.infoRow}>
            <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>Build</Text>
            <Text style={[styles.infoValue, { color: colors.text }]}>100</Text>
          </View>
        </View>

        {/* Description */}
        <View style={[styles.card, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
          <Text style={[styles.descriptionTitle, { color: colors.text }]}>About the App</Text>
          <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
            TradingView is a comprehensive trading platform designed to help you make informed trading decisions with AI-powered insights, real-time market data, and advanced charting tools.
          </Text>
          <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
            Our platform provides seamless trading experience with secure transactions, detailed analytics, and personalized AI bot subscriptions to enhance your trading strategy.
          </Text>
        </View>

        {/* Features */}
        <View style={[styles.card, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
          <Text style={[styles.descriptionTitle, { color: colors.text }]}>Key Features</Text>
          
          <View style={styles.featureItem}>
            <Icon name="checkmark-circle" size={20} color={colors.primary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>Real-time market data and charts</Text>
          </View>
          
          <View style={styles.featureItem}>
            <Icon name="checkmark-circle" size={20} color={colors.primary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>AI-powered trading insights</Text>
          </View>
          
          <View style={styles.featureItem}>
            <Icon name="checkmark-circle" size={20} color={colors.primary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>Secure wallet and transactions</Text>
          </View>
          
          <View style={styles.featureItem}>
            <Icon name="checkmark-circle" size={20} color={colors.primary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>Comprehensive trading history</Text>
          </View>
          
          <View style={styles.featureItem}>
            <Icon name="checkmark-circle" size={20} color={colors.primary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>Referral rewards program</Text>
          </View>
        </View>

        {/* Copyright */}
        <Text style={[styles.copyright, { color: colors.textSecondary }]}>
          © 2024 TradingView. All rights reserved.
        </Text>

        <View style={{ height: vs(40) }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.base,
    paddingTop: vs(8),
    paddingBottom: vs(16),
    borderBottomWidth: 1,
  },
  backButton: {
    padding: spacing.xs,
  },
  headerTitle: {
    fontSize: fontSize.xl,
    fontWeight: '700',
  },
  placeholder: {
    width: 32,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.base,
  },
  logoContainer: {
    alignItems: 'center',
    marginTop: vs(32),
    marginBottom: vs(24),
  },
  logo: {
    width: 200,
    height: 80,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: spacing.base,
    marginBottom: vs(16),
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: vs(8),
  },
  infoLabel: {
    fontSize: fontSize.base,
  },
  infoValue: {
    fontSize: fontSize.base,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    marginVertical: vs(4),
  },
  descriptionTitle: {
    fontSize: fontSize.lg,
    fontWeight: '600',
    marginBottom: vs(12),
  },
  descriptionText: {
    fontSize: fontSize.base,
    lineHeight: 24,
    marginBottom: vs(12),
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: vs(12),
  },
  featureText: {
    fontSize: fontSize.base,
    marginLeft: spacing.xs,
    flex: 1,
  },
  copyright: {
    fontSize: fontSize.sm,
    textAlign: 'center',
    marginTop: vs(24),
  },
});

export default AboutScreen;
